import { FC, useEffect, useState } from 'react';
import { Field, FieldProps, FormikProps, FormikValues } from 'formik';
import { useLazyQuery } from '@apollo/client';
// @ts-ignore
import { Button, CSVLink, FieldRow, Flex, Modal, StyledFormSelectFormik } from '..';
import { ALL_PEOPLE } from '../../../graphql';

import {
  COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  COMMUNITY_MEMBER_STAKEHOLDER_TEMPLATE,
  DISTRICT_OFFICIALS_STAKEHOLDER_TEMPLATE,
  LEARNERS_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  LEARNERS_STAKEHOLDER_TEMPLATE,
  PROVINCIAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  SCHOOL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  NATIONAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  SOCIAL_SERVICES_PROFESSIONAL_STAKEHOLDER_TEMPLATE,
  STUDENT_STAKEHOLDER_TEMPLATE,
} from '../../../consts';
import {
  commonPeopleFields,
  communityMemberFields,
  districtOfficialsFields,
  commonAssociationFields,
  commonLearnerAssociationFields,
  learnersFields,
  provincialOfficialsFields,
  schoolOfficialsFields,
  nationalOfficialsFields,
  socialServicesProfessionalsFields,
  studentFields,
  FieldType as FlatFileFieldType,
} from '../../../consts/flatfileFields';
import { TemplateType } from '../../../consts/uploadTemplates';
import { Option } from '../../../utils/types';
import { formatPeopleForFlatfileTemplate } from '../../../utils';

interface Props {
  addStakeholderToOrganisation?: boolean;
  setShowPeopleOptionsModal: (value: boolean) => void;
  showPeopleOptionsModal: boolean;
  formikProps: FormikProps<FormikValues>;
  allStakeholderTypes?: Option[] | undefined;
  institutionId?: string;
}

type FormType = Option | undefined;

const getFields = (addStakeholderToOrganisation: boolean, type?: string): FlatFileFieldType[] => {
  const association = {
    'Community Members': [...commonAssociationFields],
    'District Officials': [...commonAssociationFields],
    Learners: [...commonAssociationFields, ...commonLearnerAssociationFields],
    'Provincial Officials': [...commonAssociationFields],
    'School Officials': [...commonAssociationFields],
    'National Officials': [...commonAssociationFields],
    'Social Services Professionals': [...commonAssociationFields],
    Students: [...commonAssociationFields],
  };
  const creation = {
    'Community Members': [...commonPeopleFields, ...communityMemberFields],
    'District Officials': [...commonPeopleFields, ...districtOfficialsFields],
    Learners: [...commonPeopleFields, ...learnersFields],
    'Provincial Officials': [...commonPeopleFields, ...provincialOfficialsFields],
    'School Officials': [...commonPeopleFields, ...schoolOfficialsFields],
    'National Officials': [...commonPeopleFields, ...nationalOfficialsFields],
    'Social Services Professionals': [...commonPeopleFields, ...socialServicesProfessionalsFields],
    Students: [...commonPeopleFields, ...studentFields],
  };

  if (addStakeholderToOrganisation) {
    if (!type) {
      let fields: FlatFileFieldType[] | undefined = [...commonAssociationFields, ...commonLearnerAssociationFields];
      if (!fields) fields = [];
      return fields;
    }
    let fields: FlatFileFieldType[] | undefined = association[type];
    if (!fields) fields = [];
    return fields;
  } else {
    if (!type) {
      let fields: FlatFileFieldType[] | undefined = [...commonPeopleFields, ...learnersFields];
      if (!fields) fields = [];
      return fields;
    }
    let fields: FlatFileFieldType[] | undefined = creation[type];
    if (!fields) fields = [];
    return fields;
  }
};

// @ts-ignore
const getTemplate = (
  addStakeholderToOrganisation: boolean,
  type: string,
  updateOrNew?: string,
  people?: any
): TemplateType | undefined => {
  const association = {
    'Community Members': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'District Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    Learners: LEARNERS_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'Provincial Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'School Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'National Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'Social Services Professionals': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    Students: COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  };
  const creation = {
    'Community Members': COMMUNITY_MEMBER_STAKEHOLDER_TEMPLATE,
    'District Officials': DISTRICT_OFFICIALS_STAKEHOLDER_TEMPLATE,
    Learners: LEARNERS_STAKEHOLDER_TEMPLATE,
    'Provincial Officials': PROVINCIAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'School Officials': SCHOOL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'National Officials': NATIONAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'Social Services Professionals': SOCIAL_SERVICES_PROFESSIONAL_STAKEHOLDER_TEMPLATE,
    Students: STUDENT_STAKEHOLDER_TEMPLATE,
  };

  if (addStakeholderToOrganisation) {
    let template: TemplateType | undefined = association[type];
    return template;
  } else {
    let template: TemplateType | undefined = creation[type];
    if (people) {
      if (template) {
        template = [['System Id', ...template[0]]];
        people.forEach((b: any) => {
          const row = formatPeopleForFlatfileTemplate(b);
          if (template) {
            template.push(row);
          }
        });
      }
    }

    return template;
  }
};

const TemplateModal: FC<Props> = props => {
  const addStakeholderToOrganisation =
    props.addStakeholderToOrganisation === undefined ? false : props.addStakeholderToOrganisation;
  const [people, setPeople] = useState<[{ id: string }] | any>([]);

  const [fetch, { data: peopleData, loading }] = useLazyQuery(ALL_PEOPLE, {
    variables: {
      input: {
        institutionId: props.institutionId,
      },
    },
  });

  useEffect(() => {
    if (props.institutionId) {
      fetch({
        variables: {
          input: {
            institutionId: props.institutionId,
          },
        },
      });
    }
  }, [props.institutionId, fetch]);

  useEffect(() => {
    if (peopleData && peopleData.people && peopleData.people.edges) {
      setPeople(peopleData.people.edges.map((a: any) => a.node));
    }
  }, [peopleData]);

  const template: any =
    props.formikProps.values.updateOrNew === 'new'
      ? getTemplate(addStakeholderToOrganisation, 'Learners', props.formikProps.values.updateOrNew)
      : getTemplate(addStakeholderToOrganisation, 'Learners', props.formikProps.values.updateOrNew, people);

  return (
    <Modal
      open={props.showPeopleOptionsModal}
      onClose={() => props.setShowPeopleOptionsModal(!props.showPeopleOptionsModal)}
      heading="Upload Data"
      bodyOverflowVisible={true}
    >
      <Flex direction="column" size="1">
        <Flex direction="column" size="1" flexWrap="nowrap" justify="space-around" minHeight={170}>
          <FieldRow>
            <Field
              name="typeId"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormSelectFormik
                  {...field}
                  field={field}
                  label="Stakeholder Type"
                  form={props.formikProps}
                  isClearable={true}
                  changeHandler={(item: any) => {
                    if (item) {
                      const fields = getFields(addStakeholderToOrganisation, item.label);
                      props.formikProps.setFieldValue('fields', fields);
                    } else {
                      props.formikProps.setFieldValue('fields', []);
                    }
                  }}
                  mr={0}
                  options={props.allStakeholderTypes}
                />
              )}
            />
          </FieldRow>
        </Flex>
        <FieldRow flex={0.3}>
          {!loading ? (
            <CSVLink filename={`PeopleTemplate.csv`} data={template}>
              Generate Template
            </CSVLink>
          ) : (
            <Button text="" type="button" bg="success" isLoading={loading} />
          )}
        </FieldRow>
      </Flex>
    </Modal>
  );
};

export default TemplateModal;
